<!-- the popupingEntity list that shows via Channel and Single page model Entity -->
<template>
    <!--
    注意事项
    1. 最外层 div 一定要设置 overflow: auto; 否则拖拽会显得很停顿；目前手机端支持得不算太好。
    2. Mugen Scroll 使用相关注意事项
       2.1 不要使用 mugen-scroll 的 scrollContainer 方式，即是在最外层 div 上定义 class="wrap"，然后在 mugen-scroll 上使用 croll-container="wrap" 引用，试过这种方式，不能正常的加载 next page
       2.2 should-handle 是告诉 mugen-scroll 是否要继续调用 next page，通常，本来在 loading 过程中不要调用，另外如果已经到达 last page 不要调用。
    -->
    <div>
        <div style="overflow: auto;" ref="list-wrap" class="list-wrap">
            <el-backtop :right="45"></el-backtop>
            <template v-for="chunk in entityChunks">
                <list-row>
                    <!-- 填充 list-row 中的 slots -->
                    <template v-for="(e, i) in chunk">
                        <!-- 必须使用具名插槽的方式填充，否则一个插槽内会被插入 3 个 entities，原因不明。为了编程简单，使用编码作为具名插槽的名称 -->
                        <list-cell :slot="i" :e="e" :class="['margin-bottom', i!==2 ? 'margin-right':'']" :click-handler="showEntity"></list-cell>
                    </template>
                </list-row>
            </template>
            <template>
                <no-signal
                    ref="noSignal"
                    v-if="noSignalVisible"
                    :retry-handlers="[nextPage]"
                    :retry-basis="false"
                    display-mode="horizental"></no-signal>
                <!-- offset 表示，还有多少的距离，scroll 触底，设置 offset=-50 只是修复在 PC Chrome 手机尺寸下，分页包含的 entities 是 15、或者 > 21 不下滑的问题 -->
                <mugen-scroll ref='scroll'
                              :handler="nextPage"
                              :should-handle="!loading && !isLastPage && !networkError"
                              :handle-on-mount="false"
                              :offset="-30"
                              v-if="scrollOnBody && isScrollable">
                     <loading :text="null" v-show="loading" :style-object="{fontSize: '36px', marginBottom: '20px', color: '#CB0101'}" :style-class="loadingClass"></loading>
                </mugen-scroll>
                <!-- scroll-container 必须使用 vue ref instance -->
                <mugen-scroll ref='scroll'
                              :handler="nextPage"
                              :should-handle="!loading && !isLastPage && !networkError"
                              :handle-on-mount="false"
                              scroll-container="list-wrap"
                              v-else-if="isScrollable">
                     <loading :text="null" v-show="loading" :style-object="{fontSize: '36px', marginBottom: '20px', color: '#CB0101'}" :style-class="loadingClass"></loading>
                </mugen-scroll>
            </template>
        </div>
        <!-- 之前 popup-entity 是放在 list-wrap div 元素中的，为了 fix HCF-345：真机环境下，用户喜欢列表不能完全弹出 entity，是因为此
             时的 popup entity 的行为很诡异，它的父类节点不能有任何一个是 overflow: auto/scroll 的 -->
        <popup-entity
            v-if="allowPopup"
            :z-index="20"
            :close-handler="closeHandler"
            :prev-entity-handler="prevEntityHandler"
            :next-entity-handler="nextEntityHandler"
            :has-prev="isFirst ? false : true"
            :has-next="isLast ? false : true"
        />
    </div>
</template>

<script>
    // import _ from 'lodash';  // 注意，loadash 本社就已经全局导入了，因此无需额外的 import
    import MugenScroll from '@shangyang/vue-mugen-scroll';
    import {TimeoutError, NetworkError} from '@shangyang/error';
    import {isHybridAgent, isWebAgent, pushBaiduSeo, ShowEntityMode} from "../../utils";
    import ListRow from "./ListRow";
    import PopupEntity from '../entity/PopupEntity';
    import ListCell from "./ListCell";
    import {loadingInstance, Styles, isPhoneAgent, errorMessage} from "../../utils";

    export default {
        name: "List",
        components: {
            ListCell,
            ListRow,
            PopupEntity,
            MugenScroll
        },
        props: {
            n: {
                type: Number,
                default: 3
            },
            nextPageFunc: Function,
            scrollable: {
                type: Boolean,
                default: true
            },
            scrollOnBody: {
                type: Boolean,
                default: true
            },
            loadingClass: String
        },
        data() {
            return {
                entities: [],
                // entityChunks: [],
                allowPopup: false,  // 是否允许弹出？配合 v-if，当 allowPopup == false 的时候也负责销毁 popup popupingEntity 组件。
                isFirst: false,  // 控制 popEntity 是否显示 prev button
                isLast: false,   // 控制 popEntity 是否显示 next button
                poppingEntity: null,  // caching the current popping up entity for get next/prev popupingEntity by click prev/next button of popup popupingEntity.
                loading: true,  // 务必设置为 True，否则 List 组件一旦初始化，mugen scroller 组件便会发起一次 next page 调用，此时 Channel 分页参数 code 还没有准备好。
                isLastPage: false,
                isScrollable: true,
                networkError: false, // 关键属性：表明分页过程中遇到网络错误。并且作为一个标识符让 mugen scroll 不要继续重试了，否则多条 error messages 将会展示，影响 UE
                noSignalVisible: false,
            }
        },
        computed: {
            /**
             * 记录一次血的教训，之前使用 lodash 去删除 this.entities 中的元素，结果导致 vue 的响应式失败，computed/watch 均对 this.entities
             * 的改变监控失败。
             */
            entityChunks: function(){
                return _.chunk(this.entities, this.n)
            }
        },
        mounted(){
            this.isScrollable = this.scrollable;
        },
        created() {
            console.log('list created');
            // 不能在这里调用，因为 list 是 Channel 的子组件，会在 Channel 之前创建成功，导致 Channel 还没来得及得到 nextPage 的关键参数
            // code，下面的 nextPage() 就开始调用了，从而导致无法获取 data.
            // this.nextPage(); // fetch the first page
        },
        /*
         * 下面的 activated 和 deactivated 主要是 FIX HCF-236，当 List -> Entity 然后 Entity -> List 会发现 List spinner 在转动，
         * 但是永远加载不成功！注意：子组件无法使用“组件内导航守卫”，因此无法使用 beforeRouteEnter。
         */
        deactivated () {
            console.log('list, deactivated');
            this.isScrollable = false;
            this.isLastPage = true;
            console.log('scroll has been destroied');
        },
        activated() {
            // 使用 timeout 解决移动端关闭屏幕长时间后，再次激活，不能继续 scroll 的问题
            // 必须给足延迟时间激活 scrollable
            if(this.scrollable === true) { // respect 用户传进来的行为
                setTimeout(() => {
                    this.isScrollable = true;
                    setTimeout(() => { // 当 scrollable 激活后，等待 scroll 组件被创建好后，才开始 scroll.
                        this.isLastPage = false;
                        console.log('scroll has been renabled');
                    }, 300);
                }, 300);
            }
        },
        watch: {
            // 监听路由，如果路由有变化，则执行该方法，Single Page 之间的切换因为根路径并未变化，只是参数变化，因此需要通过这里切换 Entity
            // https://router.vuejs.org/zh/guide/advanced/data-fetching.html#%E5%AF%BC%E8%88%AA%E5%AE%8C%E6%88%90%E5%90%8E%E8%8E%B7%E5%8F%96%E6%95%B0%E6%8D%AE
            // 注意，离开或者是进入都会触发这个方法，因此判断是离开还是进入，主要通过 from 和 to，如果是 from 自己那么是离开，相反这是进入。
            $route(to, from){
                /*
                 * 这个方法主要解决 BUG .... TODO
                 */
                console.log('List, the route get changed, to:', to.name, 'and from:', from.name);
            }
        },
        methods : {
            /**
             * return the index of the current popupingEntity
             * @param e, the popupingEntity
             */
            index(e) {
                for (let i = 0; i < this.entities.length; i++) {
                    // TODO refactor below huocool specific logic to be common logic
                    if(this.entities[i].shortcode == e.shortcode){
                        return i
                    }
                }
                throw Error(`e ${e.shortcode} is not existed in entities`)
            },

            /**
             * get the popupingEntity based on the shortcode
             * @param shortcode
             */
            getEntity(shortcode){
                for(let e of this.entities){
                    if(e.shortcode === shortcode){
                        return e;
                    }
                }
                throw Error(`can not found ${shortcode}`)
            },

            /**
             * return all entity ids from this.entities
             */
            getEntityShortcodes(){
                let ids = []
                for(let e of this.entities){
                    ids.push(e.shortcode);
                }
                return ids;
            },

            /**
             * ask if e has next Entity?
             * @param e
             * @return {Boolean}
             */
            async hasNext(e){
                // 若已经到了当前缓存的最后一个了，那么继续加载下一页
                if(this.index(e) === this.entities.length -1){
                    console.log('list has next, meet the last cached ones, try to fetch the next page');
                    let loading = loadingInstance(); // 在 popup entity window 翻完这一页加载下一页的时候，增加 loading 效果。
                    try{
                        await this.nextPage();
                    }finally{
                        loading.close();
                    }
                }
                let hasNext = this.index(e) === this.entities.length - 1 ? false : true;
                console.log('list hasNext:', hasNext);
                return hasNext;
            },

            /**
             * return the next one
             * @param e
             * @return next Entity or null
             */
            async next(e){
                if(await this.hasNext(e)){
                    let i = this.index(e);
                    return this.entities[i+1];
                }
                return null;
            },

            /**
             * ask if e has previous Entity?
             * @param e
             * @return {Boolean}
             */
            hasPrviouse(e){
                return this.index(e) === 0 ? false : true;
            },

            previous(e){
                if(this.hasPrviouse(e)){
                    let i = this.index(e);
                    return this.entities[i-1];
                }
                return null;
            },

            /**
             * 1. 在 List 页面弹出 Popup Entity 组件，用于显示 Entity。
             * 2. 注意，显示是在 $nextTick 中完成的，因此调用方无需再使用 $nextTick 了。
             * 3. 修复 HCF-297 List page 空白页 - 在 loading 过程中并未完成，点击了 Entity 并进入了，然后返回就会产生空白页。现在解决的
             *    思路是，在 showEntity 方法中，休眠等待 loading == false，然后再进入 Entity。
             *    修复 BUG，
             * @param {Number} the index of the entities
             */
            showEntity(shortcode){
                // 修复 HCF-297，当且仅当 loading  === false 的时候才能够进入 Entity.
                let loading = loadingInstance();
                let interval = setInterval(()=>{
                    console.log('show entity, checking loading:', this.loading);
                    if(this.loading === false){
                        console.log('show entity, the loading is false, show it');
                        window.clearInterval(interval);
                        console.log('show entity, the interval get cleared');
                        // 还必须给足够的时间，让 new page 的图片挂载。
                        setTimeout(()=>{
                            loading.close();  // 这里关闭掉，因为 showEntity 过程中还会初始化一个 loading，避免冲突。
                            this.__showEntity(shortcode);
                        }, 300)
                    }
                }, 500)
            },

            __showEntity(shortcode){
                var e = this.getEntity(shortcode); // 注意，这里直接从 List 缓存即 List.entities 中获取的，并没有发起远程调用。
                this.$store.commit('cacheEntity', e); // cache the current entity for single page mode showing
                console.log('showEntity, show entity mode', this.$store.getters.getShowEntityMode())
                var mode = this.$store.getters.getShowEntityMode();
                if(mode === ShowEntityMode.PopupPageMode){
                    // cache the popping up entity，注意：popup entity 就是从这里获取的 entity
                    this.$store.commit('setCurrentEntity', e);
                    // cache the return url, when the popping up entity closed, return to the returnUrl.
                    this.returnUrl = this.$route.path;
                    // this.$router.push('/e/entityid'); // 注意，这样的话，会导致 vue 局部刷新页面，不是我期望的
                    // 使用 replaceState，这样不但不会刷新页面，而且不会有历史记录，因为是弹出窗口，这里不记录 history 记录。
                    if(!isHybridAgent()){ // 只有 web 端需要，手机 web 端不会报错，但是在真机环境下报错。
                        history.replaceState({}, 'a post', '/e/' + e.shortcode);
                        this.pushSeos();
                    }
                    this.hasPrviouse(e) ? this.isFirst = false : this.isFirst = true;
                    async function exec(){
                        await this.hasNext(e) ? this.isLast = false: this.isLast = true;
                        this.poppingEntity = e;
                        // 注意一定要在 $nextTick 中执行 popup popupingEntity 的逻辑，否则在 pre/next popupingEntity 的时候不会显示弹出 popupingEntity 窗口
                        // $nextTick 的大致的含义是，当页面的 dom 元素准备好以后，回调。这里也就是当上述的变量都响应式到对应的 dom 上以后，才回调。
                        this.$nextTick(()=>{
                            this.allowPopup = true; // 所有准备工作完成以后，再弹出 popupingEntity 窗口
                            Styles.disableBodyScroll();
                        });
                    }
                    exec.bind(this)().catch(error => console.log(error));
                }else if(mode === ShowEntityMode.SinglePageMode){
                    console.log('swith to', shortcode);
                    // 注意，下面这种方式不会工作，见官网的说明 https://router.vuejs.org/guide/essentials/navigation.html
                    // this.$router.push({ path: '/e', params: { 'sample' + id } });
                    // this.$router.push({ path: `/e/sample${id}` });
                    this.$router.push({ name:'entity', params: { shortcode: shortcode } });
                }
            },

            prevEntityHandler(){
                this.allowPopup = false; // 先销毁 popup popupingEntity window
                let e = this.previous(this.poppingEntity);
                if(e !== null)
                    this.showEntity(e.shortcode);
            },

            nextEntityHandler(){
                async function handler(){
                    this.allowPopup = false; // 先销毁 popup popupingEntity window
                    let e = await this.next(this.poppingEntity)
                    if(e !== null)
                        this.showEntity(e.shortcode);
                }
                handler.bind(this)();
            },

            // 处理 popup popupingEntity 的 close button 点击后的回调事件！
            closeHandler(){
                this.allowPopup = false;
                if(!isHybridAgent()){  // 只有 web 端需要，手机 web 端不会报错，但是在真机环境下报错。
                    history.replaceState({}, 'list page', this.returnUrl);
                }
                this.$emit('popup-entity-closed');
            },

            // 初始化加载数据；场景有
            // 1. 第一次初始化 List 的时候，比如访问 /
            // 2. 从一个 channel code 切换到另外一个 channel code，比如从 nature -> ocean
            async initData(){
                console.log('list init data called');
                this.isLastPage = false; // reset
                this.clean();  // clean the data.
                await this.nextPage();
            },

            /**
             * 1. to resolve the async
             * 2. 手机端，用户直接关闭屏幕，等待一段事件后再次激活屏幕，这个时候 next page 容易超时报错，这个时候，需要 try catch 异常，
             *    然后将 loading 置为 false，这样便于 mugen scroll 重试 next page.
             */
            async nextPage(){
                console.log('list nextPage() called');
                this.loading = true;
                try{
                    let entities = await this.nextPageFunc(); // invoke the Channel/Profile callback to fetch new page of entiteis.
                    console.log('_next page, entities:', entities);
                    if(entities !== null && entities.length > 0){
                        console.log('entities from next page func handler: ', entities);
                        for(let e of entities){
                            this.push(e);
                        }
                    }else{
                        // 最后一页了，告诉 mugen scroll 不要再加载了
                        console.log('entities is empty from next page func handler')
                        this.isLastPage = true;
                    }
                    this.loading = false;
                    this.noSignalVisible = false;
                    this.networkError = false;
                }catch(error){
                    console.error(error)
                    /* 下面是 no signal retry 的逻辑，包含两个部分，一个是检测到是 main page 的异常，即是比如说，当 Channel 加载第一个
                     * 页面的时候就报错，这个时候极有可能其它组件比如 channel menu 或者 user 都没有准备好，因此需要作为 main page 的方式
                     * 进行 retry，通过 error.handled 字段可以判断是否已经被处理了。另外一种就是局部异常了，比如说分页到第 N 页的时候报错，
                     * 则这个时候只需要重新加载这个分页即可。
                     */
                    if (error instanceof TimeoutError || error instanceof NetworkError){
                        /* @deprecated retry here, 在 requests 中已经重试，这里通过用户点击重试 */
                        console.log('list nextPage caught timeout error or any kind of network error');
                        this.networkError = true;
                        if (error.handled === undefined) { // 如果 error.handled 存在则表示已经被主组件处理了，比如 Channel. 则不再处理，否则由 List 处理。
                            errorMessage(error.message);
                            this.noSignalVisible = true;
                        }
                    }
                    this.loading = false;
                }
            },

            /**
             * clean all data.
             */
            clean() {
                this.entities = [];
            },

            /**
             * append new e into the head of this.entities
             * @param e
             */
            add(e){
                console.log('list add entity', e.shortcode);
                if(!this.entities.includes(e)){
                    this.entities.unshift(e); // 向数组头添加元素
                }
            },

            /**
             * append new e into the head of this.entities with no duplicates
             * @param e
             */
            unshift(e){
                if(!this.entities.includes(e)){ // 这个能做到吗？经过验证是可以直接这么去重的
                    // console.log(`list unshift, add the entity ${e.shortcode} into its head'`);
                    this.entities.unshift(e); // 向数组头添加元素
                }else{
                    console.log(`list unshift, the entity ${e.shortcode} already exists'`);
                }
            },

            /**
             * append new e into the tail of this.entities with no duplicates
             * @param e
             */
            push(e){
                // console.log('list add the entity to its tail', e.shortcode);
                if(!this.entities.includes(e)){ // 这个能做到吗？
                    this.entities.push(e); // 向数组尾部添加元素
                }
            },

            /**
             * remove an e from this.entities.
             * @param e
             */
            remove(e){
                console.log('list remove entity', e.shortcode)
                this.entities.splice(this.index(e), 1);
            },

            pushSeos(){
                console.log('channel push seo, process.env.NODE_ENV:', process.env.NODE_ENV);
                if (process.env.NODE_ENV === 'production' && !isHybridAgent()){
                    pushBaiduSeo();
                    // pushSoSeo();
                }
            }
        }

    }
</script>

<style scoped>

    /* 设置 backtop 图标字体颜色 */
    .el-backtop, .el-calendar-table td.is-today{
        color: #CB0101;
    }

</style>



// WEBPACK FOOTER //
// src/components/list/List.vue